import { ISimulation, ISimulationScore, TSimulationType } from '@/types';

// ================================
// Types
// ================================

/**
 * Input contract for compliance-flag visibility.
 *
 * Design notes:
 * - `isLoading` is optional to keep this helper broadly reusable in contexts
 *   where loading state may not exist; undefined is treated as "not loading".
 * - `score` is nullable because feature-level data can be unresolved while the
 *   page loads or after API/state transitions.
 */
type ShouldShowComplianceFlagParams = {
  isLoading?: boolean;
  score?: ISimulationScore | null;
};

/**
 * Input contract for pass/fail card visibility.
 *
 * We intentionally pass only the minimum primitives required by policy so this
 * helper is decoupled from broader score/simulation object shapes.
 */
type GetPassFailCardVisibilityParams = {
  scorePass?: boolean;
  simulationType?: TSimulationType;
};

/**
 * Input contract for score-history-card visibility.
 *
 * Policy combines:
 * - global print suppression
 * - completed-certification suppression
 * - caller preference (`showScoreHistory`)
 */
type ShouldShowScoreHistoryCardParams = {
  isPrinting?: boolean;
  showScoreHistory?: boolean;
  simulation?: ISimulation | null;
};

/**
 * Input contract for next-simulation-card visibility.
 *
 * Policy combines:
 * - global print suppression
 * - absence of a next simulation
 * - completed-certification suppression
 */
type ShouldShowNextSimulationCardParams = {
  isPrinting?: boolean;
  nextSimulation?: ISimulation | null;
  simulation?: ISimulation | null;
};

/**
 * Canonical pass/fail card visibility result.
 *
 * Why explicit keys (instead of a tuple):
 * - Readability at call sites (`visibility.showPassCard`)
 * - Easier extension if policy later includes explanation metadata
 */
type PassFailCardVisibility = {
  showFailCard: boolean;
  showPassCard: boolean;
};

// ================================
// Internal Helpers
// ================================

/**
 * Returns true when the simulation context represents a completed
 * certification attempt.
 *
 * This is a shared suppression condition used by multiple visibility policies.
 */
const isCompletedCertificationSimulation = (simulation?: ISimulation | null): boolean => {
  return simulation?.type === 'certification' && simulation?.status === 'completed';
};

/**
 * Returns true when pass/fail cards are meaningful for the simulation type.
 *
 * Domain rule:
 * - Pass/fail cards are only applicable for certification and practice.
 * - Role-play and unknown types should not display either card.
 */
const isPassFailApplicableSimulationType = (simulationType?: TSimulationType): boolean => {
  return simulationType === 'certification' || simulationType === 'practice';
};

// ================================
// Public Domain Policy
// ================================

/**
 * Determines whether the compliance flag should be shown.
 *
 * Rule order:
 * 1. While loading, always hide the flag (prevents transient noise).
 * 2. Show when any score category is a violation.
 * 3. Show when compliance reminders include at least one item.
 * 4. Otherwise hide.
 *
 * Behavior parity:
 * - Preserves existing hook semantics where either source of compliance signal
 *   (violation category or reminder item) is sufficient.
 */
export const shouldShowComplianceFlag = ({
  isLoading,
  score,
}: ShouldShowComplianceFlagParams): boolean => {
  // Loading has highest precedence to avoid flicker before data settles.
  if (isLoading) return false;

  // Legacy (v1-style) compliance signal derived from category typing.
  const hasViolationCategory = score?.categories?.some((category) => category.type === 'violation');

  // Newer (v2-style) compliance signal derived from reminder content.
  // Explicit boolean coercion keeps parity with prior truthiness behavior.
  const hasComplianceReminderItems = score?.complianceReminders?.items.length ? true : false;

  // Any qualifying signal enables the flag.
  return (hasViolationCategory || hasComplianceReminderItems) ?? false;
};

/**
 * Computes pass/fail card visibility for the current simulation context.
 *
 * Rule order:
 * 1. If pass/fail is not applicable to simulation type, hide both cards.
 * 2. Otherwise, pass card follows `scorePass`.
 * 3. Fail card is the inverse of `scorePass` (including undefined -> true).
 *
 * Important parity note:
 * - For applicable simulation types with `scorePass` undefined, this helper
 *   intentionally returns `{ showPassCard: false, showFailCard: true }` to
 *   match pre-extraction behavior.
 */
export const getPassFailCardVisibility = ({
  scorePass,
  simulationType,
}: GetPassFailCardVisibilityParams): PassFailCardVisibility => {
  // Do not render pass/fail framing where pass semantics are not meaningful.
  if (!isPassFailApplicableSimulationType(simulationType)) {
    return {
      showFailCard: false,
      showPassCard: false,
    };
  }

  // Applicable simulation types mirror legacy boolean behavior.
  return {
    showFailCard: !scorePass,
    showPassCard: scorePass ?? false,
  };
};

/**
 * Determines whether the score history card should be shown.
 *
 * Suppression precedence:
 * 1. Hide during printing.
 * 2. Hide for completed certification contexts.
 * 3. Otherwise follow the caller's `showScoreHistory` preference.
 */
export const shouldShowScoreHistoryCard = ({
  isPrinting,
  showScoreHistory,
  simulation,
}: ShouldShowScoreHistoryCardParams): boolean => {
  // Printing mode strips non-essential navigation/history surfaces.
  if (isPrinting) return false;

  // Completed certifications intentionally collapse supplemental cards.
  if (isCompletedCertificationSimulation(simulation)) return false;

  // Final gate remains caller-controlled.
  return Boolean(showScoreHistory);
};

/**
 * Determines whether the next simulation card should be shown.
 *
 * Suppression precedence:
 * 1. Hide during printing.
 * 2. Hide when there is no next simulation.
 * 3. Hide for completed certification contexts.
 * 4. Otherwise show.
 */
export const shouldShowNextSimulationCard = ({
  isPrinting,
  nextSimulation,
  simulation,
}: ShouldShowNextSimulationCardParams): boolean => {
  // Print view focuses on current score output only.
  if (isPrinting) return false;

  // Without a destination simulation there is no actionable card to render.
  if (!nextSimulation) return false;

  // Completed certification contexts suppress forward navigation card.
  if (isCompletedCertificationSimulation(simulation)) return false;

  // No suppression rules matched, so card can be displayed.
  return true;
};
